/**
 * Property Panel (Right Sidebar)
 * Consolidates all editing controls for the selected level.
 */

import { useCallback, useMemo, useState, useEffect } from 'react';
import type { NumFormat } from '../../schema/core/types';
import {
  useEditorContext,
  useLevelBinding,
  useStyleBinding,
  useCurrentLevelStyleId,
  useCurrentLevel,
  useBatchUpdate,
} from '../../schema/hooks/useEditor';
import { halfPointsToPt, charsToTwips, twipsToChars, twipsToLines, linesToTwips, twipsToPx } from '../../schema/utils/units';
import { useLanguage } from '../../i18nContext';

// ============ Constants ============

type ListType = 'none' | 'numbered' | 'bullet';

const NUMBERED_FORMAT_OPTIONS: { value: NumFormat; label: string; icon: string }[] = [
  { value: 'decimal', label: '1, 2, 3', icon: '1' },
  { value: 'lowerLetter', label: 'a, b, c', icon: 'a' },
  { value: 'upperLetter', label: 'A, B, C', icon: 'A' },
  { value: 'lowerRoman', label: 'i, ii, iii', icon: 'i' },
  { value: 'upperRoman', label: 'I, II, III', icon: 'I' },
  { value: 'chineseCountingThousand', label: '', icon: '一' },
];

const BULLET_OPTIONS = [
  '•', '○', '●', '◦', '▪', '▫',
  '■', '□', '▢', '▣', '▤', '▥',
  '◆', '◇', '◈', '◉', '◊', '○',
  '→', '⇒', '➔', '➜', '➝', '➞',
  '✓', '✔', '✗', '✘', '☐', '☑',
  '★', '☆', '✦', '✧', '※', '⁕',
  '−', '–', '—', '~', '≈', '∼',
  '›', '»', '❯', '▸', '▹', '►',
];

const SUFFIX_OPTIONS = [
    { value: '.', label: '.' },
    { value: ')', label: ')' },
    { value: '、', label: '、' },
    { value: ':', label: ':' },
    { value: '>', label: '>' },
    { value: '-', label: '-' },
    { value: ' ', label: '(Space)' },
];

const FONT_SIZES = [8, 9, 10, 10.5, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];
const CJK_FONTS = ['微软雅黑', '宋体', '黑体', '楷体', '仿宋', '华文楷体', '华文宋体', '华文黑体', '华文仿宋', '思源黑体', '思源宋体'];
const LATIN_FONTS = ['Arial', 'Calibri', 'Times New Roman', 'Georgia', 'Verdana', 'Tahoma', 'Trebuchet MS', 'Helvetica', 'Garamond', 'Cambria'];

// ============ Helpers ============

function extractSuffix(pattern: string): string {
  if (!pattern) return '.';
  const suffix = pattern.replace(/%\d/g, '');
  // 不要trim，保留空格后缀
  return suffix || '.';
}

function buildPattern(level: number, suffix: string): string {
  return `%${level + 1}${suffix}`;
}

// ============ Sub-Components ============

const Section = ({ title, children, defaultOpen = true }: { title: string, children: React.ReactNode, defaultOpen?: boolean }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <div className="property-section">
            <div className="section-header" onClick={() => setIsOpen(!isOpen)}>
                <span className="section-title">{title}</span>
                <span className={`section-arrow ${isOpen ? 'open' : ''}`}>▼</span>
            </div>
            {isOpen && <div className="section-content">{children}</div>}
        </div>
    );
};

// ============ Main Component ============

export function PropertyPanel() {
  const { state } = useEditorContext();
  const batchUpdate = useBatchUpdate();
  const styleId = useCurrentLevelStyleId();
  const level = useCurrentLevel();
  const { t } = useLanguage();
  const tc = t.templateCenter;

  // Basic Bindings
  const [numFmt, setNumFmt] = useLevelBinding<NumFormat>(['numFmt']);
  const [pattern, setPattern] = useLevelBinding<string>(['pattern']);

  // Calculated State
  const listType: ListType = useMemo(() => {
    if (pattern === '' || pattern === null || pattern === undefined) return 'none';
    if (numFmt === 'bullet') return 'bullet';
    return 'numbered';
  }, [numFmt, pattern]);

  const currentSuffix = useMemo(() => {
    if (listType !== 'numbered') return '.';
    return extractSuffix(pattern);
  }, [listType, pattern]);

  // Custom bullet 本地缓冲，允许临时为空而不触发 listType 变 'none'
  const [customBullet, setCustomBullet] = useState(pattern || '');
  useEffect(() => {
    setCustomBullet(pattern || '');
  }, [pattern]);

  // Handlers
  const handleListTypeChange = useCallback((newType: ListType) => {
    const basePath = [
      'numberingSchemas',
      `AbstractNum_${state.selectedAbstractNumId}`,
      'levels',
      String(state.activeLevel),
    ];

    if (newType === 'none') {
      batchUpdate([
        { path: [...basePath, 'numFmt'], value: 'bullet' },
        { path: [...basePath, 'pattern'], value: '' },
      ]);
    } else if (newType === 'bullet') {
      batchUpdate([
        { path: [...basePath, 'numFmt'], value: 'bullet' },
        { path: [...basePath, 'pattern'], value: '•' },
      ]);
    } else {
      batchUpdate([
        { path: [...basePath, 'numFmt'], value: 'decimal' },
        { path: [...basePath, 'pattern'], value: buildPattern(state.activeLevel, '.') },
      ]);
    }
  }, [batchUpdate, state.selectedAbstractNumId, state.activeLevel]);

  const handleSuffixChange = useCallback((suffix: string) => {
    setPattern(buildPattern(state.activeLevel, suffix));
  }, [setPattern, state.activeLevel]);


  // Typography Logic
  const [fontAscii, setFontAscii] = useStyleBinding<string | null>(styleId || '', ['font', 'ascii']);
  const [fontEastAsia, setFontEastAsia] = useStyleBinding<string | null>(styleId || '', ['font', 'eastAsia']);
  const [fontSize, setFontSize] = useStyleBinding<number | undefined>(styleId || '', ['fontSize']);
  const [bold, setBold] = useStyleBinding<boolean | undefined>(styleId || '', ['bold']);
  const [italic, setItalic] = useStyleBinding<boolean | undefined>(styleId || '', ['italic']);
  const [underline, setUnderline] = useStyleBinding<boolean | undefined>(styleId || '', ['underline']);

  // Spacing Logic needed for Font Size change (to keep visual line height consistent)
  const [spacingBefore] = useStyleBinding<string | null>(styleId || '', ['spacing', 'before']);
  const [spacingAfter] = useStyleBinding<string | null>(styleId || '', ['spacing', 'after']);
  const [spacingLine, setSpacingLine] = useStyleBinding<string | null>(styleId || '', ['spacing', 'line']);
  const [indentLeft, setIndentLeft] = useStyleBinding<number | undefined>(styleId || '', ['paragraph', 'indent', 'left']); // Should use numbering indent actually

  const fontSizePt = fontSize ? halfPointsToPt(fontSize) : 10.5;

  const handleFontSizeChange = useCallback((newSizePt: number) => {
    if (!styleId) return;
    const oldFontSizePt = fontSizePt;
    const newFontSizeHalfPt = newSizePt * 2;

    const lineTwips = spacingLine ? parseInt(spacingLine, 10) : 240;
    const lineSpacingMultiplier = lineTwips / 240;

    const updates: Array<{ path: string[]; value: unknown }> = [
      { path: ['paragraphStyles', styleId, 'fontSize'], value: newFontSizeHalfPt }
    ];

    if (spacingBefore) {
        const beforeTwips = parseInt(spacingBefore, 10);
        const beforeLines = twipsToLines(beforeTwips, oldFontSizePt, lineSpacingMultiplier);
        const newBeforeTwips = linesToTwips(beforeLines, newSizePt, lineSpacingMultiplier);
        updates.push({ path: ['paragraphStyles', styleId, 'spacing', 'before'], value: String(Math.round(newBeforeTwips)) });
    }
    if (spacingAfter) {
        const afterTwips = parseInt(spacingAfter, 10);
        const afterLines = twipsToLines(afterTwips, oldFontSizePt, lineSpacingMultiplier);
        const newAfterTwips = linesToTwips(afterLines, newSizePt, lineSpacingMultiplier);
        updates.push({ path: ['paragraphStyles', styleId, 'spacing', 'after'], value: String(Math.round(newAfterTwips)) });
    }

    // 联动更新缩进值，保持字符数不变
    if (level) {
        const oldLeft = level.indent.left;
        const oldHanging = level.indent.hanging;

        // 转换为字符数
        const leftChars = twipsToChars(oldLeft, oldFontSizePt);
        const hangingChars = twipsToChars(oldHanging, oldFontSizePt);

        // 使用新字号重新计算twips
        const newLeft = charsToTwips(leftChars, newSizePt);
        const newHanging = charsToTwips(hangingChars, newSizePt);

        const indentBasePath = [
            'numberingSchemas',
            `AbstractNum_${state.selectedAbstractNumId}`,
            'levels',
            String(state.activeLevel),
            'indent',
        ];

        updates.push({ path: [...indentBasePath, 'left'], value: Math.round(newLeft) });
        updates.push({ path: [...indentBasePath, 'hanging'], value: Math.round(newHanging) });
    }

    batchUpdate(updates);
  }, [fontSizePt, spacingBefore, spacingAfter, spacingLine, styleId, batchUpdate, level, state.selectedAbstractNumId, state.activeLevel]);


  // Indentation & Spacing Logic (Sliders)
  const schemaLeft = level?.indent.left ?? 440;
  const schemaHanging = level?.indent.hanging ?? 440;

  const symbolPosition = schemaLeft - schemaHanging;
  const symbolPositionChars = twipsToChars(Math.max(0, symbolPosition), fontSizePt);
  const hangingChars = twipsToChars(Math.max(0, schemaHanging), fontSizePt);
  const textIndentChars = twipsToChars(schemaLeft, fontSizePt);

  const basePath = useMemo(() => [
    'numberingSchemas',
    `AbstractNum_${state.selectedAbstractNumId}`,
    'levels',
    String(state.activeLevel),
    'indent',
  ], [state.selectedAbstractNumId, state.activeLevel]);

  const handleSymbolPositionCharsChange = useCallback((chars: number) => {
    const newSymbolPosTwips = charsToTwips(chars, fontSizePt);
    const safeSymbolPos = Math.max(0, newSymbolPosTwips);

    if (listType === 'none') {
       batchUpdate([{ path: [...basePath, 'left'], value: safeSymbolPos }]);
    } else {
       const newLeft = safeSymbolPos + schemaHanging;
       batchUpdate([{ path: [...basePath, 'left'], value: newLeft }]);
    }
  }, [batchUpdate, basePath, fontSizePt, schemaHanging, listType]);

  const handleHangingCharsChange = useCallback((chars: number) => {
     const newHangingTwips = charsToTwips(chars, fontSizePt);

     const currentSymbolPos = symbolPosition;
     if (currentSymbolPos < 0) {
         batchUpdate([
             { path: [...basePath, 'left'], value: newHangingTwips },
             { path: [...basePath, 'hanging'], value: newHangingTwips },
         ]);
     } else {
         const newLeft = currentSymbolPos + newHangingTwips;
         batchUpdate([
             { path: [...basePath, 'left'], value: newLeft },
             { path: [...basePath, 'hanging'], value: newHangingTwips },
         ]);
     }
  }, [batchUpdate, basePath, fontSizePt, symbolPosition]);


  // Paragraph Spacing
  const beforeTwips = spacingBefore ? parseInt(spacingBefore, 10) : 0;
  const afterTwips = spacingAfter ? parseInt(spacingAfter, 10) : 0;
  const lineTwips = spacingLine ? parseInt(spacingLine, 10) : 260; // Default 1.08x (Word default)
  const lineSpacingMultiplier = lineTwips / 240;

  const beforeLines = twipsToLines(beforeTwips, fontSizePt, lineSpacingMultiplier);
  const afterLines = twipsToLines(afterTwips, fontSizePt, lineSpacingMultiplier);

  const handleBeforeLinesChange = useCallback((lines: number) => {
     if (!styleId) return;
     const newTwips = linesToTwips(lines, fontSizePt, lineSpacingMultiplier);
     const updates = [{ path: ['paragraphStyles', styleId, 'spacing', 'before'], value: String(Math.max(0, Math.round(newTwips))) }];
     batchUpdate(updates);
  }, [fontSizePt, lineSpacingMultiplier, styleId, batchUpdate]);

  const handleAfterLinesChange = useCallback((lines: number) => {
     if (!styleId) return;
     const newTwips = linesToTwips(lines, fontSizePt, lineSpacingMultiplier);
     const updates = [{ path: ['paragraphStyles', styleId, 'spacing', 'after'], value: String(Math.max(0, Math.round(newTwips))) }];
     batchUpdate(updates);
  }, [fontSizePt, lineSpacingMultiplier, styleId, batchUpdate]);


  return (
    <div className="property-panel">

      {/* 1. Format (List Type & Numbering) */}
      <Section title={tc.listFormat}>
        <div className="control-group">
            <div className="segmented-control">
                <button className={listType === 'none' ? 'active' : ''} onClick={() => handleListTypeChange('none')}>{tc.none}</button>
                <button className={listType === 'numbered' ? 'active' : ''} onClick={() => handleListTypeChange('numbered')}>{tc.number}</button>
                <button className={listType === 'bullet' ? 'active' : ''} onClick={() => handleListTypeChange('bullet')}>{tc.bullet}</button>
            </div>
        </div>

        {listType === 'numbered' && (
            <>
                <div className="control-group">
                    <label>{tc.numberStyle}</label>
                    <select value={numFmt} onChange={(e) => setNumFmt(e.target.value as NumFormat)}>
                        {NUMBERED_FORMAT_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.value === 'chineseCountingThousand' ? t.workspace.chineseNumbers : opt.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="control-group">
                    <label>{tc.suffix}</label>
                    <div className="suffix-options">
                        {SUFFIX_OPTIONS.map(opt => (
                            <button
                                key={opt.value}
                                className={currentSuffix === opt.value ? 'active' : ''}
                                onClick={() => handleSuffixChange(opt.value)}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
            </>
        )}

        {listType === 'bullet' && (
            <>
                <div className="control-group">
                    <label>{tc.symbol}</label>
                    <div className="bullet-grid-small">
                         {BULLET_OPTIONS.slice(0, 20).map(b => (
                             <button
                                key={b}
                                className={pattern === b ? 'active' : ''}
                                onClick={() => setPattern(b)}
                             >{b}</button>
                         ))}
                    </div>
                </div>
                <div className="control-group">
                    <label>{tc.custom}</label>
                    <input
                        type="text"
                        value={customBullet}
                        onChange={(e) => {
                          setCustomBullet(e.target.value);
                          if (e.target.value) {
                            setPattern(e.target.value);
                          }
                        }}
                        onBlur={() => {
                          if (!customBullet) {
                            setCustomBullet(pattern || '•');
                          }
                        }}
                        maxLength={2}
                        style={{ width: '60px', textAlign: 'center' }}
                    />
                </div>
            </>
        )}
      </Section>

      {/* 2. Typography */}
      {styleId ? (
          <Section title={tc.typography}>
            <div className="control-group">
                <label>{tc.fontEastAsia}</label>
                <select value={fontEastAsia || ''} onChange={(e) => setFontEastAsia(e.target.value || null)}>
                    <option value="">Default</option>
                    {CJK_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
            </div>
            <div className="control-group">
                <label>{tc.fontLatin}</label>
                <select value={fontAscii || ''} onChange={(e) => setFontAscii(e.target.value || null)}>
                    <option value="">Default</option>
                    {LATIN_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
            </div>
            <div className="control-row">
                <div className="control-item grow">
                     <label>{tc.sizeStyle}</label>
                     <select value={fontSizePt} onChange={(e) => handleFontSizeChange(parseFloat(e.target.value))}>
                         {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                     </select>
                </div>
            </div>
            <div className="control-row button-row">
                 <button className={bold ? 'active' : ''} onClick={() => setBold(!bold)}><b>B</b></button>
                 <button className={italic ? 'active' : ''} onClick={() => setItalic(!italic)}><i>I</i></button>
                 <button className={underline ? 'active' : ''} onClick={() => setUnderline(!underline)}><u>U</u></button>
            </div>
          </Section>
      ) : (
          <div className="warning-box">No paragraph style linked.</div>
      )}

      {/* 3. Layout */}
      <Section title={tc.layoutSpacing}>
         <div className="control-group">
             <div className="slider-header">
                 <label>{listType === 'none' ? tc.textIndent : tc.symbolPosition}</label>
                 <span>{(listType === 'none' ? textIndentChars : symbolPositionChars).toFixed(1)} {tc.unitCh}</span>
             </div>
             <input
                type="range" min="0" max="20" step="0.5"
                value={listType === 'none' ? textIndentChars : symbolPositionChars}
                onChange={(e) => handleSymbolPositionCharsChange(parseFloat(e.target.value))}
             />
         </div>
         {listType !== 'none' && (
             <div className="control-group">
                 <div className="slider-header">
                     <label>{tc.textGap}</label>
                     <span>{hangingChars.toFixed(1)} {tc.unitCh}</span>
                 </div>
                 <input
                    type="range" min="0" max="10" step="0.5"
                    value={hangingChars}
                    onChange={(e) => handleHangingCharsChange(parseFloat(e.target.value))}
                 />
             </div>
         )}

         {styleId && (
             <>
                <div className="divider"></div>
                <div className="control-group">
                     <div className="slider-header">
                         <label>{tc.spaceBefore}</label>
                         <span>{beforeLines.toFixed(1)} {tc.unitLines}</span>
                     </div>
                     <input
                        type="range" min="0" max="5" step="0.1"
                        value={beforeLines}
                        onChange={(e) => handleBeforeLinesChange(parseFloat(e.target.value))}
                     />
                 </div>
                 <div className="control-group">
                     <div className="slider-header">
                         <label>{tc.spaceAfter}</label>
                         <span>{afterLines.toFixed(1)} {tc.unitLines}</span>
                     </div>
                     <input
                        type="range" min="0" max="5" step="0.1"
                        value={afterLines}
                        onChange={(e) => handleAfterLinesChange(parseFloat(e.target.value))}
                     />
                 </div>
                 <div className="control-group">
                     <label>{tc.lineSpacing}</label>
                     <select value={lineTwips} onChange={(e) => setSpacingLine(e.target.value)}>
                        <option value="240">{tc.lineSingle}</option>
                        <option value="260">{tc.lineWordDefault}</option>
                        <option value="288">{tc.line12}</option>
                        <option value="360">{tc.line15}</option>
                        <option value="480">{tc.lineDouble}</option>
                     </select>
                 </div>
             </>
         )}
      </Section>

    </div>
  );
}
